import {
  Box,
  CircularProgress,
  Drawer,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseCircle } from "iconsax-react";
import { useNavigate } from "@tanstack/react-router";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetCustomer, useGetOrders } from "@/api";
import { GetOrdersOrder } from "@/api/schemas/getOrdersOrder";
import { GetOrdersSortBy } from "@/api/schemas/getOrdersSortBy";
import { SefanoIconButton, StatusBadge } from "@/components/Atoms";
import { orderStatusDisplayMap, getOrderDisplayFields } from "@/lib/dashboard/orderStatus";
import { formatPersianCurrency, formatPersianDate, formatPersianNumber } from "@/lib/format/persian";
import { useSessionStore } from "@/stores/session.store";
import type { CustomerDetailDrawerProps } from "./CustomerDetailDrawer.types";
import * as styles from "./CustomerDetailDrawer.styles";

export type { CustomerDetailDrawerProps } from "./CustomerDetailDrawer.types";

function getInitial(name: string): string {
  return name.trim().charAt(0) || "—";
}

export const CustomerDetailDrawer = memo(function CustomerDetailDrawer({
  open,
  customerId,
  onClose,
}: CustomerDetailDrawerProps) {
  const { t } = useTranslation(["customers", "home"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const activeStoreId = useSessionStore((state) => state.activeStoreId);

  const { data: customerResponse, isLoading: isCustomerLoading } = useGetCustomer(
    customerId ?? 0,
    {
      query: { enabled: open && customerId !== null },
    },
  );

  const customer = customerResponse?.data?.data;

  const ordersParams = useMemo(() => {
    if (activeStoreId === null || !customer?.phone_number) return undefined;

    return {
      store_id: activeStoreId,
      search: customer.phone_number,
      per_page: 50,
      page: 1,
      sort_by: GetOrdersSortBy.createdAt,
      order: GetOrdersOrder.DESC,
    };
  }, [activeStoreId, customer]);

  const { data: ordersResponse, isLoading: isOrdersLoading } = useGetOrders(ordersParams, {
    query: {
      enabled: open && customerId !== null && !!ordersParams,
    },
  });

  const orders = useMemo(() => {
    const all = ordersResponse?.data?.data ?? [];
    if (customerId === null) return [];
    return all.filter((order) => order.customer_id === customerId);
  }, [ordersResponse, customerId]);

  const handleOrderClick = useCallback(
    (orderId: number) => {
      onClose();
      void navigate({ to: "/orders/$orderId", params: { orderId: String(orderId) } });
    },
    [navigate, onClose],
  );

  const isLoading = isCustomerLoading;
  const drawerAnchor = isMobile ? "bottom" : "right";

  return (
    <Drawer
      anchor={drawerAnchor}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: { sx: styles.drawerPaper(isMobile) },
      }}
    >
      {isLoading ? (
        <Box sx={styles.loadingWrap}>
          <CircularProgress size={32} />
        </Box>
      ) : customer ? (
        <>
          <Box sx={styles.header}>
            <Box sx={styles.profileRow}>
              <Box sx={styles.avatar}>{getInitial(customer.full_name)}</Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {customer.full_name}
                </Typography>
                <Typography variant="body2" sx={styles.phone}>
                  {customer.phone_number}
                </Typography>
              </Box>
            </Box>
            <SefanoIconButton variant="ghost" aria-label={t("customers:detail.title")} onClick={onClose}>
              <CloseCircle size={22} variant="Linear" />
            </SefanoIconButton>
          </Box>

          <Box sx={styles.statsRow}>
            <Box sx={styles.infoCard}>
              <Typography sx={styles.infoLabel}>{t("customers:detail.orderCount")}</Typography>
              <Typography sx={styles.infoValue}>
                {formatPersianNumber(orders.length)}
              </Typography>
            </Box>
            <Box sx={styles.infoCard}>
              <Typography sx={styles.infoLabel}>{t("customers:detail.totalSpent")}</Typography>
              <Typography sx={styles.infoValue}>
                {formatPersianCurrency(
                  orders.reduce((sum, order) => sum + (order.final_payable_amount ?? 0), 0),
                ).replace(" ریال", "")}
              </Typography>
            </Box>
            <Box sx={styles.infoCard}>
              <Typography sx={styles.infoLabel}>{t("customers:detail.joinedAt")}</Typography>
              <Typography sx={styles.infoValue}>
                {customer.created_at
                  ? formatPersianDate(new Date(customer.created_at))
                  : "—"}
              </Typography>
            </Box>
            <Box sx={styles.infoCard}>
              <Typography sx={styles.infoLabel}>{t("customers:detail.lastOrder")}</Typography>
              <Typography sx={styles.infoValue}>
                {orders[0] ? getOrderDisplayFields(orders[0]).date : "—"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={styles.infoLabel}>{t("customers:detail.address")}</Typography>
            <Typography sx={{ fontSize: "0.84375rem", lineHeight: 1.8 }}>
              {customer.address || "—"}
            </Typography>
          </Box>

          <Box sx={{ height: 1, bgcolor: "divider" }} />

          <Typography sx={styles.sectionTitle}>{t("customers:detail.orderHistory")}</Typography>

          {isOrdersLoading ? (
            <Box sx={styles.loadingWrap}>
              <CircularProgress size={28} />
            </Box>
          ) : orders.length === 0 ? (
            <Typography sx={styles.emptyOrders}>—</Typography>
          ) : (
            <Box sx={styles.orderList}>
              {orders.map((order) => {
                const { date } = getOrderDisplayFields(order);
                const statusDisplay = orderStatusDisplayMap[order.status];
                return (
                  <Box
                    key={order.id}
                    sx={styles.orderItem}
                    onClick={() => handleOrderClick(order.id)}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        #{order.order_number ?? order.id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {date}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatPersianCurrency(order.final_payable_amount ?? 0)}
                      </Typography>
                      <StatusBadge
                        color={statusDisplay.color}
                        size="sm"
                        label={t(`home:orders.status.${order.status}`)}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </>
      ) : null}
    </Drawer>
  );
});
